/**
 * Tennis Position Recommendation Rules
 *
 * Tennis lineups are a singles/doubles ladder (S1-S6, D1-D3) rather than
 * fixed skill positions. Requirements descend down each ladder so a player's
 * top-scoring eligible slot naturally lands at the highest rung they qualify for.
 */

import type { PositionRule } from '../positionRecommendations';

/**
 * Tennis Position Rules
 */
export const TENNIS_POSITION_RULES: PositionRule[] = [
  // SINGLES LADDER - favors speed/stamina/agility (covering the court alone)
  {
    position: 'S1',
    tier: 'premium',
    description: 'Singles 1 - Requires elite speed, stamina, and court coverage',
    requirements: [
      { attribute: 'speed', minValue: 8, required: true },
      { attribute: 'stamina', minValue: 8, required: true },
      { attribute: 'agility', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const speed = attrs.get('speed') || 0;
      const stamina = attrs.get('stamina') || 0;
      const agility = attrs.get('agility') || 0;
      const strength = attrs.get('strength') || 0;
      const focus = attrs.get('focus') || 0;
      return speed * 1.8 + stamina * 1.8 + agility * 1.3 + strength * 1.0 + focus * 1.1;
    },
  },
  {
    position: 'S2',
    tier: 'standard',
    description: 'Singles 2 - Requires strong speed and stamina',
    requirements: [
      { attribute: 'speed', minValue: 7, required: true },
      { attribute: 'stamina', minValue: 7, required: true },
    ],
    scoreFormula: (attrs) => {
      const speed = attrs.get('speed') || 0;
      const stamina = attrs.get('stamina') || 0;
      const agility = attrs.get('agility') || 0;
      const focus = attrs.get('focus') || 0;
      return speed * 1.6 + stamina * 1.6 + agility * 1.2 + focus * 1.0;
    },
  },
  {
    position: 'S3',
    tier: 'standard',
    description: 'Singles 3 - Requires solid speed and stamina',
    requirements: [
      { attribute: 'speed', minValue: 6, required: true },
      { attribute: 'stamina', minValue: 6, required: true },
    ],
    scoreFormula: (attrs) => {
      const speed = attrs.get('speed') || 0;
      const stamina = attrs.get('stamina') || 0;
      const agility = attrs.get('agility') || 0;
      return speed * 1.5 + stamina * 1.5 + agility * 1.1;
    },
  },
  {
    position: 'S4',
    tier: 'easier',
    description: 'Singles 4 - Developing singles player',
    requirements: [
      { attribute: 'speed', minValue: 5, required: true },
      { attribute: 'stamina', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const speed = attrs.get('speed') || 0;
      const stamina = attrs.get('stamina') || 0;
      const agility = attrs.get('agility') || 0;
      return speed * 1.4 + stamina * 1.3 + agility * 1.0;
    },
  },
  {
    position: 'S5',
    tier: 'easier',
    description: 'Singles 5 - Developing singles player',
    requirements: [
      { attribute: 'speed', minValue: 4, required: true },
    ],
    scoreFormula: (attrs) => {
      const speed = attrs.get('speed') || 0;
      const stamina = attrs.get('stamina') || 0;
      return speed * 1.3 + stamina * 1.2;
    },
  },
  {
    position: 'S6',
    tier: 'easier',
    description: 'Singles 6 - Entry-level singles slot',
    requirements: [
      { attribute: 'speed', minValue: 3, required: true },
    ],
    scoreFormula: (attrs) => {
      const speed = attrs.get('speed') || 0;
      const stamina = attrs.get('stamina') || 0;
      return speed * 1.2 + stamina * 1.1;
    },
  },

  // DOUBLES LADDER - favors positioning/awareness/reflexes (net play & teamwork)
  {
    position: 'D1',
    tier: 'premium',
    description: 'Doubles 1 - Requires elite positioning, awareness, and net reflexes',
    requirements: [
      { attribute: 'positioning', minValue: 8, required: true },
      { attribute: 'awareness', minValue: 7, required: true },
      { attribute: 'agility', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const awareness = attrs.get('awareness') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      const focus = attrs.get('focus') || 0;
      return positioning * 1.8 + awareness * 1.6 + agility * 1.4 + speed * 1.0 + focus * 1.0;
    },
  },
  {
    position: 'D2',
    tier: 'standard',
    description: 'Doubles 2 - Requires solid positioning and awareness',
    requirements: [
      { attribute: 'positioning', minValue: 6, required: true },
      { attribute: 'awareness', minValue: 6, required: true },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const awareness = attrs.get('awareness') || 0;
      const agility = attrs.get('agility') || 0;
      return positioning * 1.6 + awareness * 1.5 + agility * 1.2;
    },
  },
  {
    position: 'D3',
    tier: 'easier',
    description: 'Doubles 3 - Developing doubles player',
    requirements: [
      { attribute: 'positioning', minValue: 5, required: true },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const awareness = attrs.get('awareness') || 0;
      return positioning * 1.5 + awareness * 1.3;
    },
  },
];
